import React, { useEffect, useState } from "react";
import InputMask from "react-input-mask";
import { useSearchParams, useNavigate } from "react-router-dom";
import AnimationRevealPage from "helpers/AnimationRevealPage.js";
import tw from "twin.macro";
import styled from "styled-components";
import {css} from "styled-components/macro"; //eslint-disable-line
import bg_illustration from "images/laptop_sky.png";
import illustration from "images/Mascots.png";
import logo from "images/lra_pink.svg";
import { ReactComponent as LoginIcon } from "feather-icons/dist/icons/log-in.svg";
import { ReactComponent as CloseIcon } from "feather-icons/dist/icons/x.svg";
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import { PrimaryButton } from "../../components/misc/Buttons.js"
import { api } from "supabase/database/api.ts";
import { createClient } from "supabase/client.ts";
import { toast } from 'react-toastify';
import {
  Container, LogoLink, LogoImage, MainContent,
  Heading, FormContainer, Form, Input, SubmitButton, StyledModal, ModalHeading, ModalText
} from "./AuthStyles";

const MainContainer = tw.div`lg:w-full p-2 justify-center`;
const Content = styled.div`
  ${props => `background-image: url("${props.imageSrc}");`}
  ${tw`max-w-screen-xl m-0 sm:mx-20 sm:my-16 bg-white text-gray-900 shadow sm:rounded-lg flex-col justify-center flex-1`}
  ${tw`sm:rounded-r-lg sm:rounded-l-lg flex flex-col text-center lg:flex justify-center bg-contain bg-center bg-cover bg-no-repeat`}
`;
const OptionButton = styled.button`
  ${tw`mt-5 tracking-wide font-semibold bg-primary-500 text-gray-100 w-full py-3 rounded-lg hover:bg-primary-900 transition-all duration-300 ease-in-out flex items-center justify-center focus:shadow-outline focus:outline-none`}
  .icon {
    ${tw`w-3 h-3 -ml-2`}
  }
  .text {
    ${tw`ml-1 text-sm`}
  }
`;
const IllustrationContainer = styled.div`
  ${props => `background-image: url("${props.imageSrc}");`}
  ${tw`sm:rounded-r-lg sm:rounded-l-lg flex flex-col text-center hidden lg:flex justify-center bg-contain bg-center bg-cover bg-no-repeat`}
`;
const MascotImage = tw.img`h-[180px] mx-auto mt-4`;
const HighlightedText = tw.span`bg-primary-500 text-gray-100 px-4 transform -skew-x-12 inline-block`;
const CloseModalButton = tw.button`absolute top-0 right-0 mt-8 mr-8 hocus:text-primary-500`;
const HeroHeading = styled.h1`
  ${tw`text-6xl text-center sm:text-4xl lg:text-5xl xl:text-6xl font-black text-white leading-snug mb-4 mr-4`}
  text-shadow: 2px 2px 2px rgba(0, 0, 0, 0.9);
  span {
    ${tw`inline-block mt-2`}
  }
`;
const OptionsContainer = tw.div`mx-auto max-w-xs flex`;
const OptionButtonWrapper = styled.div`
  width: 50%;
`;

export default ({
  logoLinkUrl = "#",
  illustrationImageSrc = illustration,
  headingText = "LightRainAir",
  submitButtonText = "Sign In",
  SubmitButtonIcon = LoginIcon,
  forgotPasswordUrl = "/forgot-password",
  signupUrl = "/register",

}) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const redirectUrl = searchParams.get('redirect');
  const [scj_id, setScjId] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [session, setSession] = useState(null)
  const [result, setResult] = useState("");
  const [message, setMessage] = useState("");
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [loggingIn, setLoggingIn] = useState(false);
  const [hasRedirected, setHasRedirected] = useState(false);
  const supabaseClient = createClient();

  const toggleModal = () => setModalIsOpen(!modalIsOpen);
  
  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };
  
  useEffect(() => {
    supabaseClient.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      if (session && !hasRedirected) {
        setHasRedirected(true);
        navigate(redirectUrl || "/home", { replace: true });
      }
    })

    const { data: listener } = supabaseClient.auth.onAuthStateChange((_event, session) => {
      setSession(session)
    })

    return () => {
      listener?.subscription.unsubscribe();
    }
  }, [supabaseClient, navigate, redirectUrl, hasRedirected]);

  const handleLogin = async (event) => {
    event.preventDefault();
    setLoggingIn(true);

    try {
      const login_result = await api.authQueries.Login({ scj_id, password });
      setResult(login_result[0].result);
      setMessage(login_result[0].message);

      if (login_result[0].result === "Success") {
        toast.success('Login successful! Redirecting...');
        setTimeout(() => {
          window.history.replaceState(null, '', '/home');
          navigate(redirectUrl || "/home", { replace: true });
        }, 1000);
      } else {
        setModalIsOpen(!modalIsOpen);
      }
    } catch (error) {
      toast.error('Login failed. Please try again.');
    } finally {
      setLoggingIn(false);
    }
  };

  return (
    <AnimationRevealPage>
      <Container>
        <Content imageSrc={bg_illustration}>
          <IllustrationContainer>
              <MascotImage src={illustrationImageSrc} />
              <HeroHeading>Welcome to LightRainAir</HeroHeading>
          </IllustrationContainer>
          <MainContainer>
            <MainContent>
              <Heading>{<>Sign in to <HighlightedText>{headingText}</HighlightedText></>}</Heading>
              <FormContainer>
                <Form onSubmit={handleLogin}>
                  <InputMask mask={"99999999-99999"} type="text" value={scj_id} onChange={(e) => setScjId(e.target.value)}  maskChar={null} maskPlaceholder={null}>
                    <Input type="scj_id" pattern="^\d{8}-\d{5}$" placeholder="ID Number (00400314-12345)" value={scj_id} required/>
                  </InputMask>
                  <div tw="mt-6 relative">
                    <Input type={showPassword ? "text" : "password"} placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} required tw="pr-10"/>
                    <button type="button" onClick={togglePasswordVisibility} tw="absolute top-1/2 transform -translate-y-1/2 -translate-x-8 text-primary-500 bg-transparent border-none cursor-pointer">{showPassword ? <FaEyeSlash/> : <FaEye/>}</button>
                  </div>
                  <SubmitButton type="submit" >
                    <SubmitButtonIcon className="icon" />
                    <span className="text">{loggingIn === true ? "Signing in..." : submitButtonText}</span>
                  </SubmitButton>
                </Form>
                <OptionsContainer>
                  <OptionButtonWrapper style={{ marginRight: '5px'}}>
                  <a href={forgotPasswordUrl}>
                    <OptionButton>
                      <span className="text">{"Forgot Password?"}</span>
                    </OptionButton>
                  </a>
                  </OptionButtonWrapper>
                  {"   "}
                  <OptionButtonWrapper style={{ marginLeft: '5px'}}>
                  <a href={signupUrl}>
                    <OptionButton>
                      <span className="text">{"Register"}</span>
                    </OptionButton>
                  </a>
                  </OptionButtonWrapper>
                </OptionsContainer>
                  <div tw="w-full mx-auto max-w-xs">
                    <p tw="mt-2 text-sm text-gray-600 text-right">
                      Don't have an account?
                    </p>
                  </div>
              </FormContainer>
              <StyledModal
                isOpen={modalIsOpen}
                onRequestClose={toggleModal}
                shouldCloseOnOverlayClick={true}
              >
                <CloseModalButton onClick={toggleModal}>
                  <CloseIcon tw="w-6 h-6" />
                </CloseModalButton>
                <MainContent>
                  <LogoLink href={logoLinkUrl}>
                    <LogoImage src={logo} />
                  </LogoLink>
                  <ModalHeading>{`${result}`}</ModalHeading>
                  <ModalText>{message}</ModalText>
                  { (result !== "Success") ?
                    <p tw="text-center">
                      <PrimaryButton onClick={toggleModal}>Back</PrimaryButton>
                    </p> : ""
                  }
                </MainContent>
              </StyledModal>
            </MainContent>
          </MainContainer>
        </Content>
      </Container>
    </AnimationRevealPage>
  )
};